// src/pages/hr/HRAIAssistant.jsx
import React, { useState } from "react";
import { HR_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";

const PANEL_LABEL = "HR Panel";

const QUICK_PROMPTS = [
  "Summarize this month's attrition risk",
  "Which department has the lowest attendance?",
  "Suggest interview questions for a React Developer",
  "What are best practices for employee onboarding?",
  "Generate a job description for QA Engineer",
];

export default function HRAIAssistant() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hello! I'm your AI HR Assistant 🤖. I can help with recruitment, policy queries, workforce analytics, and more. How can I assist you today?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  function sendMessage(text) {
    const userMsg = text || input.trim();
    if (!userMsg) return;
    setMessages((prev) => [...prev, { role: "user", text: userMsg }]);
    setInput("");
    setLoading(true);
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: `This is a demo response for: "${userMsg}". In production, this would connect to an AI model (e.g., Gemini) to provide intelligent HR insights, policy answers, and workforce recommendations.`,
        },
      ]);
      setLoading(false);
    }, 1200);
  }

  return (
    <Layout navItems={HR_NAV} panelLabel={PANEL_LABEL}>
      <style>{`
        .ai-wrap { display: flex; flex-direction: column; gap: 1.25rem; height: calc(100vh - 160px); }
        .ai-chat { flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 0.85rem; padding: 1rem; background: var(--bg-secondary, #f9fafb); border-radius: 12px; border: 1px solid var(--border, #e5e7eb); }
        .ai-msg { display: flex; gap: 0.65rem; align-items: flex-start; max-width: 85%; }
        .ai-msg--user { align-self: flex-end; flex-direction: row-reverse; }
        .ai-msg-bubble { padding: 0.75rem 1rem; border-radius: 12px; font-size: 0.87rem; line-height: 1.55; }
        .ai-msg--assistant .ai-msg-bubble { background: #fff; border: 1px solid var(--border, #e5e7eb); color: var(--text-primary, #111); border-radius: 0 12px 12px 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
        .ai-msg--user .ai-msg-bubble { background: linear-gradient(135deg, #1b4b31, #3da068); color: #fff; border-radius: 12px 0 12px 12px; }
        .ai-avatar { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 1rem; flex-shrink: 0; }
        .ai-avatar--bot { background: linear-gradient(135deg, #1b4b31, #3da068); color: #fff; }
        .ai-avatar--user { background: #e5e7eb; }
        .ai-input-row { display: flex; gap: 0.65rem; }
        .ai-input { flex: 1; padding: 0.75rem 1rem; border: 1px solid var(--border, #d1d5db); border-radius: 10px; font-size: 0.87rem; outline: none; background: var(--bg-card, #fff); color: var(--text-primary, #111); }
        .ai-input:focus { border-color: #1b4b31; box-shadow: 0 0 0 3px rgba(27,75,49,0.1); }
        .ai-send-btn { padding: 0.75rem 1.25rem; background: linear-gradient(135deg, #1b4b31, #3da068); color: #fff; border: none; border-radius: 10px; font-weight: 700; cursor: pointer; font-size: 0.87rem; transition: opacity 0.2s; }
        .ai-send-btn:hover { opacity: 0.9; }
        .ai-send-btn:disabled { opacity: 0.5; cursor: default; }
        .ai-quick-prompts { display: flex; flex-wrap: wrap; gap: 0.5rem; }
        .ai-quick-chip { padding: 0.35rem 0.85rem; background: var(--bg-card, #fff); border: 1px solid var(--border, #d1d5db); border-radius: 99px; font-size: 0.78rem; cursor: pointer; color: var(--text-primary, #111); font-weight: 500; transition: all 0.2s; }
        .ai-quick-chip:hover { background: var(--brand-primary); color: #fff; border-color: var(--brand-primary); }
        .ai-typing { display: flex; gap: 4px; padding: 0.75rem 1rem; }
        .ai-typing span { width: 8px; height: 8px; border-radius: 50%; background: #aaa; animation: typing 1s infinite; }
        .ai-typing span:nth-child(2) { animation-delay: 0.2s; }
        .ai-typing span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes typing { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.85); } 40% { opacity: 1; transform: scale(1.1); } }
      `}</style>

      <div className="page-header">
        <div>
          <h1 className="page-title">AI HR Assistant</h1>
          <p className="page-subtitle">AI-powered HR insights, recruitment tools, and workforce analysis</p>
        </div>
        <div style={{ background: "#d1fae5", color: "#065f46", padding: "0.4rem 0.9rem", borderRadius: 8, fontSize: "0.78rem", fontWeight: 700 }}>
          🤖 Powered by AI
        </div>
      </div>

      <div className="ai-wrap">
        <div>
          <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginBottom: "0.5rem", fontWeight: 600 }}>💡 Quick Prompts</div>
          <div className="ai-quick-prompts">
            {QUICK_PROMPTS.map((p) => (
              <button key={p} className="ai-quick-chip" onClick={() => sendMessage(p)}>{p}</button>
            ))}
          </div>
        </div>

        <div className="ai-chat">
          {messages.map((m, i) => (
            <div key={i} className={`ai-msg ai-msg--${m.role}`}>
              <div className={`ai-avatar ai-avatar--${m.role === "assistant" ? "bot" : "user"}`}>
                {m.role === "assistant" ? "🤖" : "👤"}
              </div>
              <div className="ai-msg-bubble">{m.text}</div>
            </div>
          ))}
          {loading && (
            <div className="ai-msg ai-msg--assistant">
              <div className="ai-avatar ai-avatar--bot">🤖</div>
              <div className="ai-msg-bubble">
                <div className="ai-typing">
                  <span /><span /><span />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="ai-input-row">
          <input
            type="text"
            className="ai-input"
            placeholder="Ask me anything about your workforce, policies, or recruitment…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            disabled={loading}
          />
          <button className="ai-send-btn" onClick={() => sendMessage()} disabled={loading || !input.trim()}>
            Send ✈️
          </button>
        </div>
      </div>
    </Layout>
  );
}
